
import {
  FileDocument, fileDocumentName, FileDocumentStatus, Label, Time,
} from '@/model';
import IconButton from '@/components/IconButton.vue';
import LabelChip from '@/components/LabelChip.vue';
import Vue from 'vue';
import labels, { labelSorter } from '@/store/labels';
import { documentLabelTypes as configuredDocumentLabelTypes } from '@/store/ui-config';
import { notEmpty } from '@/util/array-util';
import { RouteNames } from '@/router/route-names';

const validActions = ['editDocument', 'showDocument', 'showSummaries', 'showFragments'] as const;
type Action = typeof validActions[number];

export default Vue.extend({
  name: 'DocumentHeader',
  components: { IconButton, LabelChip },
  props: {
    document: { type: Object as () => FileDocument, required: true },
    documentLabels: { type: Boolean, default: false },
    showDocument: { type: Boolean, default: false },
    showSummaries: { type: Boolean, default: false },
    showFragments: { type: Boolean, default: false },
  },
  computed: {
    publishedYear(): number | undefined {
      return this.document.published ? Time.year(this.document.published) : undefined;
    },
    documentLabelTypes(): readonly string[] {
      return configuredDocumentLabelTypes();
    },
    labels(): Label[] {
      const allLabels = this.document?.labels
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty) ?? [];
      const visibleLabels = this.documentLabelTypes.length > 0
        ? allLabels.filter((label) => this.documentLabelTypes.includes(label.type))
        : allLabels;
      return visibleLabels.sort(labelSorter(['type', 'length', 'alpha']));
    },
    actions(): Action[] {
      return (
        [
          ...this.customActions,
          (this.showDocument ? 'showDocument' : undefined),
          (this.showSummaries ? 'showSummaries' : undefined),
          (this.showFragments ? 'showFragments' : undefined),
        ] as const
      ).filter(notEmpty);
    },
    customActions(): Action[] {
      return Object.keys(this.$listeners).filter((k) => !!validActions.find((a) => a === k)) as Action[];
    },
  },
  methods: {
    fileDocumentName,
    doShowDocument(document: FileDocument) {
      if (this.customActions.includes('showDocument')) {
        this.$emit('showDocument', document);
      } else {
        this.$router.push({ name: RouteNames.DocumentResult, params: { documentId: `${document.id}` } });
      }
    },
    doShowSummaries(document: FileDocument) {
      if (this.customActions.includes('showSummaries')) {
        this.$emit('showSummaries', document);
      } else {
        this.$router.push({ name: RouteNames.Summaries, query: { documentId: `${document.id}` } });
      }
    },
    doShowFragments(document: FileDocument) {
      if (this.customActions.includes('showFragments')) {
        this.$emit('showFragments', document);
      } else {
        this.$router.push({ name: RouteNames.Selection, query: { documentId: `${document.id}` } });
      }
    },
    statusName(status: FileDocumentStatus) {
      return FileDocumentStatus.name(status);
    },
  },
});
